import type { UseQueryResult } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";

import { QueryErrorState } from "@/api/QueryErrorState";
import { QueryPendingState } from "@/api/QueryPendingState";
import {
  classifySectionFailure,
  queryKeys,
  type ClinicalSectionSummary,
  type ClinicalSummaryItem,
  type ClinicalSummarySectionSlug,
  type SectionFailure,
} from "@/api/hooks";
import type {
  LibraryAsset,
  LibraryAssetList,
  ProjectAdoptions,
} from "@/api/library";
import { useCapability } from "@/auth/SessionContext";
import { adoptionRefusal, type AdoptionDecision } from "@/lib/adoptions";
import { sectionReviewRows, settledRead } from "@/lib/sectionAdoption";
import {
  AdoptionDecisionDialog,
  adoptionDecisionReady,
} from "@/ui/adoptions/AdoptionDecisionDialog";
import { EmptyState, ErrorState, UnavailableState } from "@/ui/states";

import {
  SectionReviewList,
  type SectionReviewActions,
  type SectionReviewGrouping,
  type SectionReviewItemActions,
} from "./SectionReviewList";

/**
 * The primary clinical review body for one Project section (#488).
 *
 * The state holder for `SectionReviewList`, in the arrangement
 * `SectionAdoptionPanel` established: this component renders every state the
 * bounded read can be in and holds the decision it raises, while the
 * presentational half stays prop-driven so its states are reachable in a test
 * without a service being coaxed into them. The read itself is the caller's, as
 * the pin set and the catalog already were — see the last section below.
 *
 * ## Why the failure arms are the specific ones and not `QueryErrorState` alone
 *
 * `SectionDiagnostics` routes everything through the shared vocabulary because
 * it served six slugs with one set of copy and had nothing to say about a
 * projection identity. This body has the opposite requirement: #488 asks for
 * Pathways and Alerts to show "honest empty/adoption states", which is only
 * meaningful if `availability: "empty"` — a successful read of a section that
 * genuinely carries nothing — is distinguishable from a section that could not
 * be read at all. So `empty` is a first-class state here, and the failures are
 * classified with `classifySectionFailure`, the same classifier
 * `SummaryContent` uses, so a stale projection and an unreachable engine do not
 * collapse into one "could not load".
 *
 * ## The empty state is not a promise that the section is empty forever
 *
 * `empty` says the composed section carries no clinical items right now. It
 * does NOT say the project adopts nothing — the adoption panel above this body
 * answers that from a different read, and it is deliberately the one that keeps
 * speaking when this one fails.
 *
 * ## IT OWNS THE DECISION STATE, AND NOT THE WRITE
 *
 * The amendment requires the bounded rows to carry canonical Adopt/ellipsis
 * actions, so the rows raise decisions and this layer holds them — the same
 * arrangement `SectionAdoptionPanel` and `ProjectAdoptions` use, and for the
 * same reason: `SectionReviewList` is prop-driven precisely so its states are
 * reachable without a service, and a component that opened its own dialog would
 * have to read the pin set to do it.
 *
 * `AdoptionDecisionDialog` is reused UNCHANGED. It is #372's confirmation: it
 * computes the whole-set `PUT /adoptions` replacement from the set it loaded,
 * names the exact version being adopted and any pin that would be displaced,
 * and submits under the `adoptionsVersion` it read. There is still exactly ONE
 * route into changing an adoption; this is not a second one.
 *
 * ## Why the pin set, the catalog AND the summary arrive as props
 *
 * The same rule `SectionAdoptionPanel` states: several surfaces on this route
 * read the project's pin set, and they must not be able to disagree. The caller
 * holds one `useProjectAdoptionsQuery` and one `useLibraryAssetsQuery` and
 * passes both down, so the adoption block above and the rows below observe one
 * value rather than two cache observers of the same key.
 *
 * `summaryQuery` joined them in round 2, for a reason that is the same rule
 * pointed the other way. The adoption panel above must not offer a SECOND
 * update/remove control for an item whose canonical row is rendered here, and
 * the only fact that answers "is a canonical row present" is this response. So
 * `SectionDiagnostics` holds the one read and hands it to both surfaces —
 * rather than the panel acquiring an observer of a key this component already
 * watches, which is the arrangement the paragraph above exists to forbid.
 */
export function SectionReview({
  projectId,
  section,
  sectionLabel,
  summaryQuery,
  adoptions,
  catalog,
  policyUnread,
  grouping,
}: {
  projectId: string;
  section: ClinicalSummarySectionSlug;
  sectionLabel: string;
  /**
   * The caller's single bounded-review read. Never re-fetched here.
   *
   * `projectId` and `section` stay as props beside it because the pending state
   * names the key it is waiting on, and because the failure copy names the
   * section rather than the response.
   */
  summaryQuery: UseQueryResult<ClinicalSectionSummary>;
  /** The caller's single pin-set read. Never re-fetched here. */
  adoptions: UseQueryResult<ProjectAdoptions>;
  /** The caller's catalog read, archived rows included. */
  catalog: UseQueryResult<LibraryAssetList>;
  /**
   * Whether the SERVED cardinality policy has not been read (#634).
   *
   * A BOOLEAN FROM THE CALLER, on this component's own one-read discipline:
   * every query here is held by `SectionDiagnostics` and passed down, and this
   * file is deliberately renderable with no `ApiClientProvider` at all — a hook
   * added here would end that.
   *
   * It gates the row's ADOPT and nothing else. A pin on a family the server
   * bounds at one is a replacement, and `AdoptionDecisionDialog` cannot say
   * which pin it displaces without the document — so an unread policy becomes a
   * stated refusal rather than a control whose only outcome is that refusal
   * (ADR 0001). It deliberately does NOT join `decidable`: gating the ROW on it
   * would take the removal with it, and the un-pin the service's own diagnosis
   * instructs is exactly the recovery this body exists to preserve (#612,
   * `MC-PRJ-07`).
   */
  policyUnread: boolean;
  /**
   * Named groups and row controls for these rows, or absent (#855).
   *
   * From the CALLER, on this component's one-read discipline: the Pathways
   * groups are derived from the service's own flow selection, which is a read
   * `SectionDiagnostics` holds. Passing it down keeps this file renderable with
   * no `ApiClientProvider`, and keeps the rows' grouping and the flow controls
   * above them derived from ONE observation of that read rather than two.
   */
  grouping?: SectionReviewGrouping | undefined;
}) {
  const write = useCapability("draft.write");
  const [decision, setDecision] = useState<AdoptionDecision | null>(null);

  // ONE ELIGIBILITY RULE FOR THE WRITE CONTROLS, AND IT IS THE DIALOG'S OWN.
  //
  // Round 1 gated these on the CURRENT error classification — terminal fails
  // closed, retryable keeps the cached answer — which is the rule
  // `queryClient.ts` states for RENDERING data, and it is still the right rule
  // there. It is the wrong rule for offering a WRITE control. A retryable 503
  // landing on a refetch leaves `.data` in place while the result becomes
  // `status: "error"`, so the rows kept their actions while
  // `AdoptionDecisionDialog` — whose gate is `isSuccess` on both reads — could
  // only open onto its load error with the confirmation disabled. Two halves of
  // one decision disagreeing about whether it can be made (Codex, #570 round 2).
  //
  // So the question is asked once, by the component that would have to answer
  // it: `adoptionDecisionReady` is the dialog's exported gate. Withheld actions
  // leave the rows as review alone, and the adoption panel above still names
  // which read failed and offers the retry — a second recovery here would be
  // two controls for one failure.
  const decidable = adoptionDecisionReady(adoptions, catalog);

  // THE ROWS' INPUTS ARE READ SEPARATELY, on the same `isSuccess` rule
  // (`settledRead`), because the two reads are NOT one fact (#615, PR #663
  // review). The pin set alone names each pin's id, family and exact version,
  // so a settled pin set yields the stored rows whether or not the catalog
  // answered; the catalog enriches a row with its name and lock and, below,
  // gates its write controls. Gating the rows themselves on `decidable` — as
  // the first cut did — meant the audited composition failure and a catalog
  // outage arriving together erased the one row the recovery contract
  // requires, with the exact pin known from `GET /adoptions` all along.
  const pinSet = settledRead(adoptions);
  const pins = pinSet?.items ?? null;
  // THE TOKEN THAT CAME WITH THOSE PINS (#618), taken off the same settled
  // result, so the comparison below can never pair a pin set from one read
  // with a token from another. Gated on the ADOPTIONS read alone — not
  // `decidable` — because the catalog is no part of the token comparison:
  // both halves of it come from `/adoptions` and the summary. Under
  // `decidable`, a catalog 5xx landing inside the convergence window nulled
  // the token and let a stale composed row render its version and currency
  // flags as authoritative against a loaded, disagreeing pin set (review
  // round 1) — and it fed this consumer a different gate input than
  // `SectionDiagnostics`, which derives the panel's silence set from the
  // adoptions read alone.
  const pinSetVersion = pinSet?.adoptionsVersion ?? null;
  const catalogItems = settledRead(catalog)?.items ?? null;

  // ONE ROW DERIVATION, whatever state the bounded read is in (#612). The
  // exported rule `SectionDiagnostics` also reads for the panel's silence set,
  // through the same `settledRead` inputs, so the two cannot drift. A
  // successful composition renders its own items exactly as before; every
  // other state — pending, failed, composed-empty — falls back to the STORED
  // pin set, joined to the catalog where it answered, which is the list the
  // audited dead end (`MC-PRJ-07`/`RULE-PRJ-05`/`SAFE-PRJ-06`) erased: the
  // server's diagnosis says "un-pin this asset" and the un-pin action lives on
  // a row, so the row's existence may not depend on the composition whose
  // failure it recovers.
  const rows = sectionReviewRows({
    summary: settledRead(summaryQuery),
    pins,
    pinSetVersion,
    catalog: catalogItems,
    section,
  });
  const storedItems = rows.source === "stored" && rows.items.length > 0
    ? rows.items
    : null;

  // WHY BOTH READS GATE THE CONTROLS. Every decision is a whole-set
  // replacement against the pin set, described against the catalog — so an
  // unread pin set makes the dialog fail closed, and an unread catalog leaves
  // no version to name on the control. Offering a control whose only outcome
  // is that refusal is the affordance ADR 0001 forbids, so the rows render as
  // review alone until both answer. `decidable` above is where that is
  // decided, from the dialog's own rule; the `pins`/`catalogItems` narrowing
  // beside it restates nothing — `adoptionDecisionReady` IS `isSuccess` on
  // both reads, and `settledRead` is that rule applied to their data.
  //
  // A stored row therefore carries exactly one of three things, never a
  // silent absence (#612's requirement, kept under #615's wider row set): its
  // removal, when both reads settled and the catalog names the pin; a STATED
  // refusal, when the settled catalog does not list it (`adoptionRefusal`'s
  // unknown-asset rule), or the floor or a missing capability refuses; or,
  // with the catalog unread, the menu's read-only library link alone —
  // navigation needs no catalog, because the pin names its own family.
  function listOf(
    items: readonly ClinicalSummaryItem[],
    { converging = false }: { converging?: boolean } = {},
  ): ReactNode {
    // WITHHELD WHILE CONVERGING (#618), by the rule the block already applies
    // to its own controls: every decision here is a whole-set replacement
    // computed against the pin set, and this row set was composed from a
    // DIFFERENT one. An `Adopt v6` offered from a row that still describes v5
    // is a decision over a read the screen is explicitly waiting to settle —
    // and it was half of the audited contradiction, not merely a risky
    // affordance. `canonicalItemIds` carries these ids either way, so the
    // panel above does not fill the gap with a second control.
    const actions: SectionReviewActions | null =
      converging || !decidable || pins === null || catalogItems === null
        ? null
        : {
            onDecide: setDecision,
            byItemId: new Map(
              items.map((item) => [
                item.id,
                itemActions(item, {
                  asset: catalogItems.find((asset) => asset.id === item.id),
                  held: pins.some((pin) => pin.assetId === item.id),
                  missingWriteReason: write.reason,
                  policyUnread,
                }),
              ]),
            ),
          };

    return (
      <SectionReviewList
        items={items}
        sectionLabel={sectionLabel}
        converging={converging}
        {...(actions ? { actions } : {})}
        {...(grouping ? { grouping } : {})}
      />
    );
  }

  function body(): ReactNode {
    if (summaryQuery.isPending) {
      // The stored rows render BENEATH the pending state, not instead of it:
      // the composed review is still being read and the screen says so. What
      // #612 adds is that the pins and their actions do not wait for it — a
      // clinician removing a broken pin during a slow (or never-settling)
      // composition is exactly the recovery the audit found missing.
      return (
        <>
          <QueryPendingState
            isPaused={summaryQuery.isPaused}
            failureReason={summaryQuery.failureReason}
            label={`Loading ${sectionLabel}`}
            // The SAME heading the settled failure below will render, which is
            // what `QueryPendingState` documents as its requirement: a paused
            // query and the error it settles into are one failure, and two
            // headings for it read as two different problems. Classifying
            // `failureReason` here is what lets the paused screen say the same
            // thing — a 503 parked by a hidden document states the upstream
            // failure rather than a generic "could not be loaded", which is
            // exactly the defect #327 fixed for the other surfaces.
            errorTitle={failureTitle(
              classifySectionFailure(summaryQuery.failureReason),
              sectionLabel,
            )}
            rows={storedItems ? 1 : 3}
            queryKey={queryKeys.sectionSummary(projectId, section)}
          />
          {storedItems ? listOf(storedItems) : null}
        </>
      );
    }

    if (summaryQuery.isError) {
      // The failure and the recovery, TOGETHER (#612). The classified failure
      // states what the service refused and why retrying would or would not
      // help; the stored rows beneath it are the authoritative pin list with
      // `View in Clinical library` and `Remove from project` intact. Before
      // this, the failure REPLACED the rows — which turned a correctly
      // diagnosed unplaceable pin into a dead end, because the un-pin the
      // diagnosis instructs lives on the row (`MC-PRJ-07`, `RULE-PRJ-05`,
      // `SAFE-PRJ-06`; §6.2's ruling is "the error surface must always
      // preserve a safe removal action").
      return (
        <>
          <SectionReviewFailure
            failure={classifySectionFailure(summaryQuery.error)}
            error={summaryQuery.error}
            sectionLabel={sectionLabel}
            retainedRows={storedItems !== null}
            onRetry={() => void summaryQuery.refetch()}
          />
          {storedItems ? listOf(storedItems) : null}
        </>
      );
    }

    const { availability, items } = summaryQuery.data;

    if (availability === "empty" || items.length === 0) {
      // A composed-empty answer beside a NON-EMPTY stored pin set is not an
      // empty section — it is the service declining to account for pins this
      // project verifiably holds, and the honest rendering is the pins. The
      // empty state's own copy promises "nothing is hidden", which is only
      // true when there is nothing to hide.
      if (storedItems) {
        return listOf(storedItems);
      }
      if (grouping) {
        // A GROUPED SECTION STATES ITS OWN EMPTINESS (#855). Every group
        // carries the sentence for holding no rows, and the groups themselves
        // are not a summary of the rows — they are the section's runtime
        // structure, and the Dynamic group carries the control that decides how
        // many pathways a patient runs. Replacing all of that with the generic
        // empty state hid the dynamic phase entirely on a Project that has
        // adopted nothing, which is exactly the Project that has to configure it
        // before adopting. The generic copy is also wrong there: it promises
        // "nothing is hidden" while hiding a control.
        return listOf([]);
      }
      return (
        <EmptyState
          title={
            section === "bg-sets"
              ? "No BG sets adopted yet"
              : `${sectionLabel} has no clinical content`
          }
          description={
            section === "bg-sets"
              ? "Adopt a BG set from the Clinical library to add it to this project's configuration."
              : "The Editor Service composed this section and it carries no items. Nothing failed and nothing is hidden — the base pack and this project's adoptions supply nothing here."
          }
        />
      );
    }

    return listOf(items, { converging: rows.converging });
  }

  return (
    <>
      {body()}
      {decision ? (
        <AdoptionDecisionDialog
          projectId={projectId}
          decision={decision}
          onClose={() => setDecision(null)}
        />
      ) : null}
    </>
  );
}

/**
 * What one row may offer, from what the service reported and nothing else.
 *
 * THE VERSION IS NEVER DERIVED, AND IT COMES FROM EXACTLY ONE RESPONSE.
 * `supplyState` — the summary's own field — decides which one, and there is no
 * fallback between them:
 *
 *   * **adopted** → the SUMMARY's `latestVersion`, which the service computed
 *     from the same pin it reported in the same response. Never the catalog's:
 *     the two are read at different moments, and a publish landing between them
 *     is precisely how one screen came to offer "Update … to v6" beside
 *     "Adopt v5" for a single pin (Codex, #570 round 2). One control now, and
 *     one authority behind it.
 *   * **base** → the CATALOG's `latestVersion`, because the summary reports
 *     none: `section_projection.py` writes `latest_version=None` for every base
 *     entry, so the catalog is not a fallback here but the only response that
 *     answers. It is the same value `AdoptFromLibrary` offers for an unpinned
 *     asset, out of the same catalog read.
 *
 * Where the response that answers names no version there is no Adopt, rather
 * than one invented from the other. The #612 stored fallback row relies on
 * exactly that: it is `supplyState: "adopted"` with `latestVersion: null`,
 * because its pin and the catalog's head are two reads at two moments and the
 * head can be the OLDER of the two — so it yields no Adopt here, by this rule
 * and not by a second one (`storedAdoptionRows` states why).
 *
 * THE REFUSALS ARE `lib/adoptions.ts`'s, unchanged: the missing capability, an
 * asset the catalog cannot name, the service's lock, the archived rule. `held`
 * is what separates the two things a `pin` decision means — a MOVE on an item
 * this project already pins, an ADD on one it does not — and `adoptionRefusal`
 * refuses a locked MOVE only, which is the same rule the `/adoptions` surface
 * applied to the same decision.
 */
function itemActions(
  item: ClinicalSummaryItem,
  {
    asset,
    held,
    missingWriteReason,
    policyUnread,
  }: {
    asset: LibraryAsset | undefined;
    held: boolean;
    missingWriteReason: string | null;
    /** Whether the served cardinality policy is unread — adopt only (#634). */
    policyUnread: boolean;
  },
): SectionReviewItemActions {
  // The service's own lock on this item, presented rather than re-derived. The
  // summary carries every reason it found; the refusal only needs to know that
  // one exists, and `LOCK_REASON_DETAIL` is keyed by a single reason.
  const lock = item.lock.reasons[0] ?? null;
  return {
    adoptVersion:
      item.supplyState === "adopted"
        ? item.latestVersion
        : (asset?.latestVersion ?? null),
    adoptRefusal: adoptionRefusal("pin", {
      asset,
      lock,
      held,
      missingWriteReason,
      policyUnread,
    }),
    held,
    removeRefusal: adoptionRefusal("unpin", {
      asset,
      lock,
      held: true,
      missingWriteReason,
      // NEVER the policy here. Removal is the recovery path, and a policy
      // outage may not take it away (#612).
      policyUnread: false,
    }),
  };
}

/**
 * One heading per failure kind, in ONE place.
 *
 * Both the paused branch and the settled branch read it, so the screen a
 * clinician sees while a retry is held and the screen it becomes cannot drift
 * into two different accounts of one failure. `satisfies` on
 * `SectionFailure["kind"]` is the load-bearing part: a kind added to the union
 * without a heading here stops the build rather than rendering an empty title,
 * the same mechanism `SummaryContent` uses.
 */
function failureTitle(failure: SectionFailure, sectionLabel: string): string {
  const titles = {
    unavailable: `${sectionLabel} is not available in this deployment`,
    stale: `${sectionLabel} is out of date with the deployed clinical content`,
    uncomposable: `${sectionLabel} could not be composed`,
    upstream: "The Editor Service could not reach the clinical engine",
    denied: `${sectionLabel} could not be loaded`,
    error: `${sectionLabel} could not be loaded`,
  } satisfies Record<SectionFailure["kind"], string>;
  return titles[failure.kind];
}

function SectionReviewFailure({
  failure,
  error,
  sectionLabel,
  retainedRows,
  onRetry,
}: {
  failure: SectionFailure;
  error: unknown;
  sectionLabel: string;
  /**
   * Whether the stored-adoption rows render beneath this failure (#612).
   *
   * The copy must not lie in either direction. Before #612 every arm could say
   * "nothing partial is shown in its place", because the failure replaced the
   * body wholesale. With the stored pin list retained, that sentence would deny
   * the rows on the same screen — so each arm's base description now speaks
   * only about the COMPOSED review, and this flag appends the sentence that
   * names what the rows beneath are: the stored adoptions, unaffected and
   * removable where their catalog and permission constraints allow it. When
   * no row is derivable — the project pins nothing here, or the pin set itself
   * could not be read — the failure renders alone, without the sentence.
   */
  retainedRows: boolean;
  onRetry: () => void;
}) {
  // The recovery half of the audited dead end (`MC-PRJ-07`): the server's own
  // diagnosis for an unplaceable pin instructs the clinician to un-pin it, so
  // the failure copy points at the rows that carry exactly that action.
  const retainedSentence =
    " This project's stored adoptions are listed below, unaffected: each can still be opened in the Clinical library and, where permitted, removed from the project.";
  const retained = (base: string): string =>
    retainedRows ? base + retainedSentence : base;

  switch (failure.kind) {
    case "unavailable":
      return (
        <UnavailableState
          title={failureTitle(failure, sectionLabel)}
          description={retained(
            "The Editor Service accepts this request but has not implemented the section summary here, so there is nothing to show. It resolves when the endpoint is deployed — there is nothing to retry.",
          )}
          blockedBy="HTTP 501 not_implemented — Editor Service endpoint not built in this deployment."
        />
      );
    case "stale":
      return (
        <ErrorState
          title={failureTitle(failure, sectionLabel)}
          description={retained(
            "A stored identity disagrees with what is deployed — this project's pinned base, or the draft's. Until they agree the Editor Service will not compose this section, and nothing partial is shown as the composed review. The fix is a state change made by whoever owns the base. Asking again changes nothing.",
          )}
        />
      );
    case "uncomposable":
      return (
        <ErrorState
          title={failureTitle(failure, sectionLabel)}
          description={retained(
            failure.namedField
              ? // WHETHER the service pinned the failure to one stored value,
                // never its address. The first round of #488 rendered
                // `failure.field` verbatim as a documented exception, arguing
                // that an error which cannot name its cause is dishonest. The
                // Reviewer ruled against it and the ruling is right on the
                // facts: the contract calls that value a path, the summary
                // route's own regression carries
                // `summary.en.fixed_values.title`, and the amendment bans a
                // dotted address on ANY Project route — this one included.
                //
                // What survives is the part a clinician can act on. The cause
                // is still named in kind: stored configuration that cannot be
                // placed, pinned by the service to one specific value rather
                // than to the section at large. The address itself stays in the
                // service's own error record, where the amendment says such
                // data belongs, and `classifySectionFailure` no longer carries
                // it into the browser at all.
                "Stored configuration cannot be composed into this section. The Editor Service traced it to one specific stored value — an adopted asset with no home in this pack, or a stored value with no target — and recorded which; that address is machine-level detail and is not shown on a clinical route. Asking again changes nothing until that is resolved."
              : "Stored configuration cannot be composed into this section — an adopted asset with no home in this pack, or a stored value with no target. The Editor Service did not trace it to a single value. Asking again changes nothing until that is resolved.",
          )}
        />
      );
    case "upstream":
      return (
        <ErrorState
          title={failureTitle(failure, sectionLabel)}
          description={retained(
            "Composing this section needs the pinned engine and the pack mounts, and one of them is unavailable right now. Nothing about this project's stored configuration is wrong. This one is worth asking again.",
          )}
          onRetry={onRetry}
        />
      );
    case "denied":
    case "error":
      return (
        <QueryErrorState
          error={error}
          subject={`${sectionLabel} content`}
          title={failureTitle(failure, sectionLabel)}
          description={retained(
            "The composed review is not shown from anywhere else — an empty section here would read as a section that carries no clinical content.",
          )}
          onRetry={onRetry}
        />
      );
  }
}
